import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import BoomCard from '../../../Prototype/Level/Card/BoomCard';
import { delay } from '../../../Utility/Task/TaskQueue';
import { StartAnimation } from '../AnimationHelper';
import TransformComponent from '../../../Component/Transform';
import CardComponent from '../../../Component/Level/Card';
import { Easing } from '@tweenjs/tween.js';
import { Random } from '../../../Utility/Random';
import Armature from '../../../Prototype/Armature';
import Renderable from '../../../Component/Renderable';
import { StartAudio } from '../../Audio/AudioHelper';
import { AUDIO_HAZARD_BOMB_EXPLODE } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import CardSystem from '../../Card/CardSystem';
import Interactable from '../../../Component/Interactable';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';

interface ICardBombExplosionMeta {
    system: CardSystem;
    sourceCard: BoomCard;
    targetCards: BaseCard[];
}

export class CardBombExplosion extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardBombExplosionMeta
    ): IterableIterator<number> {
        meta.sourceCard.getComponent(Renderable).visible = false;

        const armature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'CardsVFX',
            armatureName: 'BombCard_Overlay',
            animationName: 'explosion',
        });

        armature.on(DragonbonesAnimationEvent.COMPLETE, () => {
            armature.destroy();
        });

        meta.sourceCard.addChild(armature);
        animator.addChild(armature);

        const durationIn = 400;
        const durationOut = 900;

        yield* delay(durationIn);

        StartAudio(
            meta.system.scene,
            AUDIO_HAZARD_BOMB_EXPLODE,
            AudioTrackType.SFX
        );

        this.backgroundShake();

        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        // Sort based on depth so cards are never added in the wrong overlapping order.
        meta.targetCards.sort(
            (a, b) =>
                a.getComponent(CardComponent).depth -
                b.getComponent(CardComponent).depth
        );

        for (let targetCard of meta.targetCards) {
            targetCard.getComponent(Renderable).visible = true;
            targetCard.getComponent(Interactable).enabled = false;
            const transform = targetCard.getComponent(TransformComponent);
            const cardComponent = targetCard.getComponent(CardComponent);
            const tweenX = this.getTween(transform.position);
            const tweenY1 = this.getTween(transform.position);
            const tweenY2 = this.getTween(transform.position);
            const tweenRotation = this.getTween(transform);

            transform.position.set(
                sourceCardTransform.position.x,
                sourceCardTransform.position.y
            );
            transform.rotation =
                cardComponent.rotation + (4 + Random.int(-1, 1)) * Math.PI * 2;

            tweenX
                .to({ x: cardComponent.x }, durationOut)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);
            tweenY1
                .to({ y: cardComponent.y - 200 }, durationOut * 0.5)
                .easing(Easing.Quadratic.Out)
                .chain(
                    tweenY2
                        .to({ y: cardComponent.y }, durationOut * 0.5)
                        .easing(Easing.Quadratic.In)
                )
                .start(this.elapsedTime);
            tweenRotation
                .to({ rotation: cardComponent.rotation }, durationOut)
                .easing(Easing.Cubic.Out)
                .start(this.elapsedTime);
        }

        yield* delay(durationOut);

        animator.destroy();

        for (let targetCard of meta.targetCards) {
            targetCard.getComponent(Interactable).enabled = true;
        }
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 15,
                frequency: 25,
                damping: 0.9,
            });
        }
    }
}
